export class LanguageDetector {
  private static readonly SUPPORTED_LANGUAGES = [
    'en',
    'es',
    'fr',
    'de',
    'it',
    'pt',
    'nl',
    'pl',
    'sv',
    'da',
    'fi',
    'no',
    'cs',
    'sk',
    'hu',
    'ro',
    'bg',
    'hr',
    'sl',
    'et',
    'lv',
    'lt',
    'mt',
    'el',
    'cy',
  ];

  private static readonly LANGUAGE_STORAGE_KEY = '__cmp_language';

  public static detectLanguage(): string {
    const storedLanguage = this.getStoredLanguage();
    if (storedLanguage && this.isSupported(storedLanguage)) {
      return storedLanguage;
    }

    const browserLanguage = this.getBrowserLanguage();
    if (browserLanguage && this.isSupported(browserLanguage)) {
      return browserLanguage;
    }

    const navigatorLanguage = this.getNavigatorLanguage();
    if (navigatorLanguage && this.isSupported(navigatorLanguage)) {
      return navigatorLanguage;
    }

    return 'en';
  }

  public static setLanguage(language: string): void {
    if (this.isSupported(language)) {
      this.storeLanguage(language);
    }
  }

  public static getSupportedLanguages(): string[] {
    return [...this.SUPPORTED_LANGUAGES];
  }

  public static isSupported(language: string): boolean {
    const normalizedLanguage = this.normalizeLanguageCode(language);
    return this.SUPPORTED_LANGUAGES.includes(normalizedLanguage);
  }

  public static getLanguageName(languageCode: string): string {
    const names: Record<string, string> = {
      en: 'English',
      es: 'Español',
      fr: 'Français',
      de: 'Deutsch',
      it: 'Italiano',
      pt: 'Português',
      nl: 'Nederlands',
      pl: 'Polski',
      sv: 'Svenska',
      da: 'Dansk',
      fi: 'Suomi',
      no: 'Norsk',
      cs: 'Čeština',
      sk: 'Slovenčina',
      hu: 'Magyar',
      ro: 'Română',
      bg: 'Български',
      hr: 'Hrvatski',
      sl: 'Slovenščina',
      et: 'Eesti',
      lv: 'Latviešu',
      lt: 'Lietuvių',
      mt: 'Malti',
      el: 'Ελληνικά',
      cy: 'Cymraeg',
    };

    return names[languageCode] || languageCode;
  }

  private static getBrowserLanguage(): string | null {
    try {
      const language = navigator.language || (navigator as any).userLanguage;
      return language ? this.normalizeLanguageCode(language) : null;
    } catch {
      return null;
    }
  }

  private static getNavigatorLanguage(): string | null {
    try {
      const languages = navigator.languages;
      if (languages && languages.length > 0) {
        for (const language of languages) {
          const normalized = this.normalizeLanguageCode(language);
          if (this.isSupported(normalized)) {
            return normalized;
          }
        }
      }
      return null;
    } catch {
      return null;
    }
  }

  private static normalizeLanguageCode(language: string): string {
    if (!language) return 'en';

    const normalized = language.toLowerCase().split('-')[0];

    const mappings: Record<string, string> = {
      nb: 'no',
      nn: 'no',
    };

    return mappings[normalized] || normalized;
  }

  private static getStoredLanguage(): string | null {
    try {
      if (typeof localStorage !== 'undefined') {
        return localStorage.getItem(this.LANGUAGE_STORAGE_KEY);
      }
    } catch {
      // Ignore storage errors
    }
    return null;
  }

  private static storeLanguage(language: string): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(this.LANGUAGE_STORAGE_KEY, language);
      }
    } catch {
      // Ignore storage errors
    }
  }

  public static getRegionalLanguagePreferences(region: string): string[] {
    const regionalPreferences: Record<string, string[]> = {
      EU: [
        'en',
        'de',
        'fr',
        'es',
        'it',
        'pl',
        'nl',
        'pt',
        'sv',
        'da',
        'fi',
        'no',
        'cs',
        'sk',
        'hu',
        'ro',
        'bg',
        'hr',
        'sl',
        'et',
        'lv',
        'lt',
        'mt',
        'el',
        'cy',
      ],
      UK: ['en'],
      US: ['en', 'es'],
      CA: ['en', 'fr'],
      BR: ['pt'],
      IN: ['en'],
      AU: ['en'],
    };

    return regionalPreferences[region] || ['en'];
  }

  public static detectRegionalLanguage(region: string): string {
    const preferences = this.getRegionalLanguagePreferences(region);
    const detectedLanguage = this.detectLanguage();

    if (preferences.includes(detectedLanguage)) {
      return detectedLanguage;
    }

    return preferences[0];
  }
}
